// lib/api/essentia-analysis.ts
// Essentia.js를 사용한 고정밀 음악 분석

import { Essentia, EssentiaWASM } from "essentia.js";
import { createAudioContext } from "../utils/audio-context";

type EssentiaModuleFactory = () => Promise<unknown>;

let essentia: Essentia | null = null;
let isInitialized = false;

/**
 * Essentia 초기화 (한 번만 실행)
 */
async function initEssentia(): Promise<Essentia> {
  if (essentia && isInitialized) {
    return essentia;
  }

  try {
    // EssentiaWASM 초기화 - 다양한 방식 시도
    let wasmModule;
    
    console.log("🔍 EssentiaWASM 타입 확인:", typeof EssentiaWASM);
    console.log("🔍 EssentiaWASM 구조:", Object.keys(EssentiaWASM || {}));
    
    // 방법 1: EssentiaWASM.EssentiaJS가 있는지 확인
    if ((EssentiaWASM as any)?.EssentiaJS) {
      const EssentiaJS = (EssentiaWASM as any).EssentiaJS;
      if (typeof EssentiaJS === "function") {
        wasmModule = await EssentiaJS();
      } else {
        wasmModule = EssentiaJS;
      }
    }
    // 방법 2: 함수로 호출 시도
    else if (typeof EssentiaWASM === "function") {
      wasmModule = await (EssentiaWASM as EssentiaModuleFactory)();
    } 
    // 방법 3: 직접 사용 (이미 모듈인 경우)
    else if (EssentiaWASM && typeof EssentiaWASM === "object") {
      wasmModule = EssentiaWASM;
    }
    // 방법 4: default export 체크
    else if ((EssentiaWASM as any)?.default) {
      const defaultExport = (EssentiaWASM as any).default;
      wasmModule = typeof defaultExport === "function" 
        ? await defaultExport() 
        : defaultExport;
    }
    else {
      throw new Error("EssentiaWASM를 초기화할 수 없습니다. 지원되지 않는 형식입니다.");
    }

    console.log("🔍 WASM 모듈 타입:", typeof wasmModule);
    
    // Essentia 생성자에 전달
    if (!wasmModule) {
      throw new Error("WASM 모듈을 가져올 수 없습니다.");
    }
    
    essentia = new Essentia(wasmModule);
    isInitialized = true;
    console.log("✅ Essentia.js 초기화 완료");
    return essentia;
  } catch (error) {
    console.error("❌ Essentia.js 초기화 실패:", error);
    // 에러를 다시 throw하되, 더 자세한 정보 포함
    throw new Error(`Essentia.js 초기화 실패: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export interface EssentiaAnalysisResult {
  tempo: number; // BPM
  key: string; // 음계 (예: "C major", "A minor")
  keyConfidence: number; // 0-1
  energy: number; // 0-1
  danceability: number; // 0-1
  loudness: number; // dB
  speechiness: number; // 0-1
  instrumentalness: number; // 0-1
  rhythm: {
    beatsPerMinute: number;
    confidence: number;
    timeSignature: string;
  };
  spectral: {
    centroid: number;
    complexity: number;
    contrast: number[];
  };
  duration: number; // 초
}

/**
 * Essentia.js를 사용한 고급 오디오 분석
 */
export async function analyzeWithEssentia(
  audioFile: File
): Promise<EssentiaAnalysisResult> {
  console.log("🎵 Essentia.js 분석 시작:", audioFile.name);

  return new Promise(async (resolve, reject) => {
    try {
      // Essentia 초기화
      const essentiaInstance = await initEssentia();

      // 오디오 파일 로드
      const audioContext = createAudioContext();
      
      if (!audioContext) {
        reject(new Error("브라우저가 Web Audio API를 지원하지 않습니다"));
        return;
      }
      
      const arrayBuffer = await audioFile.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // 모노로 변환
      const channelData = audioBuffer.getChannelData(0);
      const audioVector = Array.from(channelData);

      console.log("📊 Essentia 특징 추출 중...");

      // 1. 템포 분석 (RhythmExtractor2013)
      const rhythmFeatures = essentiaInstance.RhythmExtractor2013(
        audioVector,
        audioBuffer.sampleRate
      );

      const tempo = rhythmFeatures.bpm;
      const beatsConfidence = rhythmFeatures.confidence || 0.8;

      console.log(`✅ 템포 감지: ${tempo} BPM (신뢰도: ${beatsConfidence})`);

      // 2. 키 분석 (KeyExtractor)
      const keyFeatures = essentiaInstance.KeyExtractor(
        audioVector,
        audioBuffer.sampleRate
      );

      const key = `${keyFeatures.key} ${keyFeatures.scale}`;
      const keyConfidence = keyFeatures.strength || 0.7;

      console.log(`✅ 음계 감지: ${key} (신뢰도: ${keyConfidence})`);

      // 3. 에너지 분석
      const energyResult = essentiaInstance.Energy(audioVector);
      const energy = Math.min(1, energyResult / 1000); // 정규화

      // 4. Loudness 분석
      const loudnessResult = essentiaInstance.Loudness(audioVector);
      const loudness = loudnessResult;

      // 5. Spectral Centroid (음색 밝기)
      const spectralCentroid = essentiaInstance.SpectralCentroid(audioVector);

      // 6. Spectral Complexity
      const spectralComplexity =
        essentiaInstance.SpectralComplexity(audioVector);

      // 7. Spectral Contrast
      const spectralContrast = essentiaInstance.SpectralContrast(audioVector);

      // 8. Danceability 추정 (템포와 리듬 기반)
      const danceability = calculateDanceability(tempo, beatsConfidence);

      // 9. Speechiness 추정 (스펙트럴 특징 기반)
      const speechiness = calculateSpeechiness(spectralCentroid);

      // 10. Instrumentalness 추정
      const instrumentalness = calculateInstrumentalness(
        energy,
        spectralComplexity
      );

      const result: EssentiaAnalysisResult = {
        tempo,
        key,
        keyConfidence,
        energy,
        danceability,
        loudness,
        speechiness,
        instrumentalness,
        rhythm: {
          beatsPerMinute: tempo,
          confidence: beatsConfidence,
          timeSignature: rhythmFeatures.timeSignature || "4/4",
        },
        spectral: {
          centroid: spectralCentroid,
          complexity: spectralComplexity,
          contrast: Array.isArray(spectralContrast)
            ? spectralContrast
            : [spectralContrast],
        },
        duration: audioBuffer.duration,
      };

      console.log("✅ Essentia.js 분석 완료:", result);
      audioContext.close();
      resolve(result);
    } catch (error) {
      console.error("❌ Essentia.js 분석 실패:", error);
      reject(error);
    }
  });
}

/**
 * Danceability 계산 (템포 기반)
 */
function calculateDanceability(tempo: number, confidence: number): number {
  // 90-130 BPM이 가장 춤추기 좋은 템포
  let danceScore = 0;

  if (tempo >= 90 && tempo <= 130) {
    danceScore = 1.0;
  } else if (tempo >= 70 && tempo < 90) {
    danceScore = 0.6 + ((tempo - 70) / 20) * 0.4;
  } else if (tempo > 130 && tempo <= 150) {
    danceScore = 1.0 - ((tempo - 130) / 20) * 0.3;
  } else if (tempo > 150) {
    danceScore = 0.5;
  } else {
    danceScore = 0.4;
  }

  return Math.min(1, danceScore * confidence);
}

/**
 * Speechiness 계산 (음성 유사도)
 */
function calculateSpeechiness(spectralCentroid: number): number {
  // 음성 주파수 대역 (300-3400 Hz)에서 높은 에너지
  if (spectralCentroid >= 300 && spectralCentroid <= 3400) {
    return 0.7;
  } else if (spectralCentroid < 300) {
    return 0.2;
  } else {
    return 0.3;
  }
}

/**
 * Instrumentalness 계산 (악기 중심도)
 */
function calculateInstrumentalness(energy: number, complexity: number): number {
  // 높은 에너지 + 복잡도 = 악기 중심
  const instrumentalScore = (energy * 0.4 + complexity * 0.6) * 0.8;
  return Math.min(1, instrumentalScore);
}

/**
 * Essentia 결과를 기존 AudioAnalysisResult 형식으로 변환
 */
export function convertEssentiaToAudioAnalysis(
  essentia: EssentiaAnalysisResult
): {
  tempo: number;
  pitchAccuracy: number;
  energyLevel: number;
  rhythmConsistency: number;
  dynamicRange: number;
  spectralCentroid: number;
  duration: number;
} {
  const clamp01 = (value: number | undefined | null): number => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return 0;
    }
    return Math.min(Math.max(value, 0), 1);
  };

  const normalizeRange = (
    value: number | undefined | null,
    max: number,
    min = 0
  ): number => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return 0;
    }
    if (max === min) {
      return 0;
    }
    const normalized = (value - min) / (max - min);
    return clamp01(normalized);
  };

  const normalizedTempo = Number.isFinite(essentia.tempo)
    ? Math.max(40, Math.min(essentia.tempo, 220))
    : 120;

  // Essentia.energy는 0~1 범위를 갖도록 이미 정규화되어 있음 (analyzeWithEssentia 참고)
  const normalizedEnergy = clamp01(essentia.energy);

  // Loudness는 dB 음수 값 (예: -6 ~ -60). 더 조용할수록 값이 작게(절댓값은 크게) 나타남.
  // 0dB를 가장 높은 다이내믹, -60dB를 가장 낮은 다이내믹으로 맵핑.
  const loudnessDb = Number.isFinite(essentia.loudness)
    ? Math.max(-60, Math.min(0, essentia.loudness))
    : -30;
  const normalizedDynamicRange = normalizeRange(loudnessDb * -1, 55, 5);

  // Spectral Centroid는 Hz 단위. 보컬/어쿠스틱 음악은 1500~2500Hz, 밝은 전자음악은 3500Hz 이상.
  // 0~4500Hz 구간으로 정규화하고 0~1로 클램프.
  const centroidHz = Number.isFinite(essentia.spectral.centroid)
    ? essentia.spectral.centroid
    : 0;
  const normalizedSpectralCentroid = normalizeRange(centroidHz, 4500, 500);

  const rhythmConfidence = clamp01(essentia.rhythm.confidence);

  return {
    tempo: normalizedTempo,
    pitchAccuracy: essentia.keyConfidence, // 키 신뢰도 → 피치 정확도
    energyLevel: normalizedEnergy,
    rhythmConsistency: rhythmConfidence,
    dynamicRange: normalizedDynamicRange,
    spectralCentroid: normalizedSpectralCentroid,
    duration: essentia.duration,
  };
}
